"use client";

import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import Script from "next/script";
import { TURNSTILE_SITE_KEY } from "@/lib/constants";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          action?: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileWidgetProps {
  onToken: (token: string) => void;
  onExpire?: () => void;
}

export interface TurnstileWidgetHandle {
  // Turnstile tokens are single-use — call this before letting the user
  // retry a submission that failed server-side, otherwise the stale token
  // is rejected as timeout-or-duplicate.
  reset: () => void;
}

// Renders a single Turnstile widget (sitekey from lib/constants) via the
// explicit JS API rather than data-sitekey, so the implicit auto-render scan
// that runs when api.js loads never double-renders this container.
const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(function TurnstileWidget(
  { onToken, onExpire },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  // The widget is rendered once, so it calls the latest handlers through refs.
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;
  });

  useEffect(() => () => {
    if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current);
    widgetIdRef.current = null;
  }, []);

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  function renderWidget() {
    if (!containerRef.current || !window.turnstile || widgetIdRef.current) return;
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      action: "turnstile-spin-v2",
      callback: (token) => onTokenRef.current(token),
      "expired-callback": () => onExpireRef.current?.(),
    });
  }

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        strategy="afterInteractive"
        onReady={renderWidget}
      />
      <div ref={containerRef} className="cf-turnstile" data-action="turnstile-spin-v2" />
    </>
  );
});

export default TurnstileWidget;